import React from "react";

import { Box, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { tradingErrorPage } from "@ippon/shared-assets";

import CustomButton from "../Buttons/Button/CustomButton";
import { useSharedColors } from "../theme/SharedComponentsTheme";

export interface PageErrorProps {
  title: string;
  message: string;
  statusCode?: string;
  imageSrc?: string;
  imageAlt?: string;
  actionLabel?: string;
  onRetry?: () => void;
}

const PageError: React.FC<PageErrorProps> = ({
  title,
  message,
  statusCode,
  imageSrc = tradingErrorPage,
  imageAlt = "",
  actionLabel = "再試行",
  onRetry,
}) => {
  const colors = useSharedColors();
  const theme = useTheme();

  const rootSx: SxProps<Theme> = {
    alignItems: "center",
    backgroundColor: theme.palette.background.default,
    boxSizing: "border-box",
    display: "flex",
    inset: 0,
    justifyContent: "center",
    overflow: "auto",
    position: "fixed",
    zIndex: theme.zIndex.modal,
  };

  return (
    <Box sx={rootSx}>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: { xs: "16px", sm: "30px" },
          maxWidth: "calc(100vw - 32px)",
          textAlign: "center",
          width: 460,
        }}
      >
        <Box
          sx={{
            mx: "auto",
            maxWidth: "100%",
            width: { xs: 240, sm: 366 },
          }}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            style={{ display: "block", width: "100%" }}
          />
        </Box>

        {statusCode && (
          <Typography
            sx={{
              color: colors.primary.main,
              fontSize: { xs: 28, sm: 60 },
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {statusCode}
          </Typography>
        )}

        <Typography
          sx={{
            color: colors.text.primary,
            fontSize: { xs: 12, sm: 24 },
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            color: colors.text.secondary,
            display: "flex",
            flexDirection: "column",
            fontSize: { xs: 10, sm: 20 },
            fontWeight: 400,
            gap: "10px",
            lineHeight: 1,
            maxWidth: "100%",
          }}
        >
          {message.split("\n").map((line, index) => (
            <span key={`${index}-${line}`}>{line || " "}</span>
          ))}
        </Typography>

        {onRetry && (
          <Box>
            <CustomButton
              buttonCategory="submit"
              hideIcon
              label={actionLabel}
              onClick={onRetry}
              sx={{
                borderRadius: "6px !important",
                fontSize: "16px !important",
                height: "40px !important",
                minWidth: "124px !important",
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageError;
